import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { PageHeader } from "@/components/app/shell/page-header";
import { AmyDraftingBanner } from "@/components/amy/amy-drafting-banner";
import { StartMoveInDialog, type MoveInTarget } from "@/components/amy/start-move-in-dialog";
import { useMoveInActions } from "@/components/amy/use-move-in-actions";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { optionalOneOf, searchPatch } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { Plus } from "@/lib/icons";
import { useCan } from "@/lib/policy";
import { AmyToday } from "./-components/amy-today";
import { LeadPickerDialog } from "./-components/lead-picker-dialog";
import { MoveInTable } from "./-components/move-in-table";

// Guarded by the /ws/amy layout, like every screen under it.
export const Route = createFileRoute("/_authenticated/ws/amy/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: "all"; status?: "closed" | "cancelled" | "all" } => ({
    view: optionalOneOf(["all"], search.view),
    status: optionalOneOf(["closed", "cancelled", "all"], search.status),
  }),
  head: () => ({
    meta: [
      { title: "Move-ins | Amy | People AI" },
      { name: "description", content: "Every move-in Amy is working, from the lead to the chart." },
    ],
  }),
  component: AmyHome,
});

type View = "today" | "all";

/** Amy's front door: Today (what waits on you, then each open move-in in a line), or every
 * move-in as a table. */
function AmyHome() {
  const { facilityId } = useFacility();
  const canWork = useCan("amy:work", facilityId === "all" ? null : facilityId);
  const { view: viewParam, status } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const view: View = viewParam ?? "today";
  const setView = (next: View) => patch({ view: next === "all" ? next : undefined });
  const [picking, setPicking] = useState(false);
  const [target, setTarget] = useState<MoveInTarget | null>(null);
  const actions = useMoveInActions();
  const start = canWork ? (
    <Button onClick={() => setPicking(true)}>
      <Plus className="size-4" />
      Start move-in
    </Button>
  ) : null;

  return (
    <div className="flex flex-col gap-5 pb-24 lg:pb-0">
      <PageHeader title="Amy" meta="Your move-ins, from the lead to the chart." actions={start} />
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        aria-label="Which view"
        value={view}
        onValueChange={(value) => (value ? setView(value as View) : undefined)}
        className="bg-card w-fit"
      >
        <ToggleGroupItem value="today" className="px-3">
          Today
        </ToggleGroupItem>
        <ToggleGroupItem value="all" className="px-3">
          All move-ins
        </ToggleGroupItem>
      </ToggleGroup>
      {facilityId === "all" ? null : <AmyDraftingBanner facilityId={facilityId} />}
      {view === "today" ? (
        <AmyToday facilityId={facilityId} />
      ) : (
        <MoveInTable
          facilityId={facilityId}
          actions={actions}
          status={status}
          onStatus={(next) => patch({ status: next })}
        />
      )}
      {picking ? (
        <LeadPickerDialog
          facilityId={facilityId}
          onClose={() => setPicking(false)}
          onPick={(picked) => {
            setPicking(false);
            setTarget(picked);
          }}
        />
      ) : null}
      {target ? <StartMoveInDialog target={target} onClose={() => setTarget(null)} /> : null}
      {actions.dialog}
    </div>
  );
}
